(ns app.front.view.payment.forte
  "Forte payment integration works in the following way:
  1. Create a button which has props needed for the payment.
  2. Load the forte.js javascript file, which modifies the button.
  3. 'Pressing the button just works'."
  (:require [app.front.entities :as entities]
            [app.front.view.payment.checkout :as checkout]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [re-frame.core-instrumented :as rf]))


(rf/reg-event-fx ::validate-response
  (fn [_ [_ data]]
    {:fx [[:dispatch [:comm/command-monitored {:command         :payment/forte-validate-response
                                               :data            {:data data}
                                               :on-success      [::validate-response.success]
                                               :on-bad-request  [::validate-response.error]
                                               :on-server-error [::validate-response.error]}]]]}))


(rf/reg-event-fx ::validate-response.success
  (fn [{:keys [db]} [_ status changes]]
    (let [nav (checkout/return-nav-fx db :view/workspace (-> db :route/match :path-params))]
      (merge nav {:dispatch [::entities/apply-changes status changes]}))))


(rf/reg-event-fx ::validate-response.error
  (fn [{:keys [db]} _]
    (let [nav (checkout/return-nav-fx db :view/workspace (-> db :route/match :path-params))]
      (merge nav {:fx [[:dispatch [:notifications/add :toast :error :error/payment-failed-title :error/forte-payment-failed]]]}))))


(defn- data->event-to-dispatch [data]
  (some-> (:event data)
          (case
            "begin"   [::set-show-loading-spinner? false]
            [::validate-response data])))


(defn- safe-json-parse [json-str]
  (try
    (js/JSON.parse json-str)
    (catch js/Error _
      nil)))


(defn- js-event->clj-data [js-event]
  (some-> js-event
          .-data
          safe-json-parse
          (js->clj :keywordize-keys true)))


(defn ^:export oncallback
  "The callback function to handle response from pressing the forte 'Pay Now' button."
  [js-event]
  (some-> js-event
          js-event->clj-data
          data->event-to-dispatch
          >evt))


(rf/reg-event-db ::set-show-loading-spinner?
  (fn [db [_ show?]]
    (assoc db ::show-loading-spinner? show?)))


(rf/reg-sub ::show-loading-spinner? :-> ::show-loading-spinner?)


(rf/reg-fx ::dispatch-with-timeout
  (fn [[evt-to-dispatch timeout-ms]]
    (js/setTimeout #(>evt evt-to-dispatch) timeout-ms)))


(rf/reg-fx ::click-forte-button
  (fn [_]
    (-> (js/document.getElementById "pay-now-button")
        .click)))


(rf/reg-cofx ::forte-button-ready?
  (fn [cofx _]
    (assoc cofx ::forte-button-ready? (-> (js/document.querySelector "[data-id=\"modal0\"]")
                                          boolean))))


(rf/reg-event-fx ::press-forte-button-when-ready
  [(rf/inject-cofx ::forte-button-ready?)]
  (fn [{::keys [forte-button-ready?]} [_ forte-src retries]]
    (cond
      forte-button-ready? {::click-forte-button nil}

      ;; Dispatch again after waiting timeout.
      (< retries 10)      {::dispatch-with-timeout [[::press-forte-button-when-ready forte-src (inc retries)] 50]}

      ;; Try to re-initialize the forte-script by starting over.
      :else               {::initialize-script-and-press-button forte-src})))


(rf/reg-fx ::initialize-script-and-press-button
  (fn [forte-src]
    (when-let [prevs (js/document.querySelectorAll "[id=\"forte-script\"")]
      (doseq [prev prevs]
        (.remove prev)))
    (let [script (.createElement js/document "script")]
      (set! (.-id script) "forte-script")
      (set! (.-src script) forte-src)
      (-> js/document .-head (.append script)))
    (>evt [::press-forte-button-when-ready forte-src 0])))


(rf/reg-event-fx ::initialize-payment
  (fn [_ [_ forte-src]]
    {::initialize-script-and-press-button forte-src}))


(defmethod checkout/payment-service-effects :payment-service/forte
  [transaction-details]
  [[:dispatch [::set-show-loading-spinner? true]]
   [:dispatch [::initialize-payment (-> transaction-details :ctx :forte-src)]]])


(defn- embedded-form [{:keys [button-props]}]
  [:div.flex.flex-col.items-center.h-full.w-full
   (when (<sub [::show-loading-spinner?])
     (cc/loading-spinner {:with-overlay true}))
   [:button.invisible
    (merge button-props {:id       "pay-now-button"
                         :callback "app.front.view.payment.forte.oncallback"})
    "Pay Now"]])


(defmethod checkout/payment-form :payment-service/forte
  [{:keys [ctx]}]
  [embedded-form ctx])
